// src/pages/CategoriasPremios.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import {
  CheckCircle2,
  Edit,
  FolderPlus,
  Layers,
  Plus,
  Power,
  Save,
  Trash2,
  X,
} from "lucide-react";
import "./CategoriasPremios.css";

export default function CategoriasPremios() {
  const [categorias, setCategorias] = useState([]);
  const [nome, setNome] = useState("");
  const [editando, setEditando] = useState(null);

  const carregar = async () => {
    const res = await axios.get("/categorias-premios");
    setCategorias(res.data || []);
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvar = async () => {
    try {
      if (!nome.trim()) {
        toast.warning("Informe o nome da categoria.");
        return;
      }

      if (editando) {
        await axios.put(`/categorias-premios/${editando.id}`, {
          nome,
          ativa: editando.ativa,
        });
        toast.success("Categoria atualizada.");
      } else {
        await axios.post("/categorias-premios", { nome });
        toast.success("Categoria criada.");
      }

      setNome("");
      setEditando(null);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao salvar categoria.");
    }
  };

  const editar = (cat) => {
    setEditando(cat);
    setNome(cat.nome);
  };

  const alternar = async (cat) => {
    try {
      await axios.put(`/categorias-premios/${cat.id}`, {
        nome: cat.nome,
        ativa: !cat.ativa,
      });
      carregar();
    } catch {
      toast.error("Erro ao alterar status.");
    }
  };

  const excluir = async (cat) => {
    if (!window.confirm(`Excluir categoria "${cat.nome}"?`)) return;

    try {
      await axios.delete(`/categorias-premios/${cat.id}`);
      toast.success("Categoria excluída.");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao excluir categoria.");
    }
  };

  const resumo = useMemo(() => {
    const total = categorias.length;
    const ativas = categorias.filter((cat) => cat.ativa).length;
    const inativas = total - ativas;

    return { total, ativas, inativas };
  }, [categorias]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  return (
    <div className="categoriasPremiosAdmin-page">
      <div className="categoriasPremiosAdmin-topbar">
        <div>
          <span className="categoriasPremiosAdmin-kicker">
            Catálogo de recompensas
          </span>

          <h2>Categorias de Prêmios</h2>

          <p>
            Crie, edite e inative categorias exibidas no catálogo e no cadastro
            de prêmios.
          </p>
        </div>
      </div>

      <div className="categoriasPremiosAdmin-cards">
        <div className="categoriasPremiosAdmin-card">
          <span>Total de categorias</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="categoriasPremiosAdmin-card success">
          <span>Ativas</span>
          <strong>{formatarNumero(resumo.ativas)}</strong>
        </div>

        <div className="categoriasPremiosAdmin-card warning">
          <span>Inativas</span>
          <strong>{formatarNumero(resumo.inativas)}</strong>
        </div>
      </div>

      <section className="categoriasPremiosAdmin-formCard">
        <div className="categoriasPremiosAdmin-cardHeader">
          <div>
            <h3>{editando ? "Editar categoria" : "Nova categoria"}</h3>
            <span>
              {editando
                ? "Altere o nome da categoria selecionada."
                : "Informe o nome para criar uma nova categoria."}
            </span>
          </div>

          <FolderPlus size={18} />
        </div>

        <div className="categoriasPremiosAdmin-form">
          <div className="categoriasPremiosAdmin-field">
            <label>Nome da categoria</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Eletrônicos"
            />
          </div>

          <button
            type="button"
            className="categoriasPremiosAdmin-btn primary"
            onClick={salvar}
          >
            {editando ? <Save size={16} /> : <Plus size={16} />}
            {editando ? "Salvar alteração" : "Criar categoria"}
          </button>

          {editando && (
            <button
              type="button"
              className="categoriasPremiosAdmin-btn secondary"
              onClick={() => {
                setEditando(null);
                setNome("");
              }}
            >
              <X size={16} />
              Cancelar
            </button>
          )}
        </div>
      </section>

      <section className="categoriasPremiosAdmin-tableCard">
        <div className="categoriasPremiosAdmin-cardHeader">
          <div>
            <h3>Lista de categorias</h3>
            <span>Gerencie status e edição das categorias cadastradas.</span>
          </div>

          <Layers size={18} />
        </div>

        <div className="categoriasPremiosAdmin-tableWrapper">
          <table className="categoriasPremiosAdmin-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {categorias.length === 0 ? (
                <tr>
                  <td colSpan="3" className="categoriasPremiosAdmin-empty">
                    Nenhuma categoria cadastrada.
                  </td>
                </tr>
              ) : (
                categorias.map((cat) => (
                  <tr key={cat.id}>
                    <td>
                      <div className="categoriasPremiosAdmin-cellMain">
                        <strong>{cat.nome}</strong>
                        <span>ID #{cat.id}</span>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`categoriasPremiosAdmin-status ${
                          cat.ativa ? "ativa" : "inativa"
                        }`}
                      >
                        {cat.ativa ? (
                          <>
                            <CheckCircle2 size={13} />
                            Ativa
                          </>
                        ) : (
                          <>
                            <Power size={13} />
                            Inativa
                          </>
                        )}
                      </span>
                    </td>

                    <td>
                      <div className="categoriasPremiosAdmin-actions">
                        <button
                          type="button"
                          className="categoriasPremiosAdmin-actionBtn edit"
                          onClick={() => editar(cat)}
                        >
                          <Edit size={14} />
                          Editar
                        </button>

                        <button
                          type="button"
                          className="categoriasPremiosAdmin-actionBtn status"
                          onClick={() => alternar(cat)}
                        >
                          <Power size={14} />
                          {cat.ativa ? "Inativar" : "Ativar"}
                        </button>

                        <button
                          type="button"
                          className="categoriasPremiosAdmin-actionBtn delete"
                          onClick={() => excluir(cat)}
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}